import { useEffect, useRef, useState } from 'react';
import { api, ApiError, formatDate, type AdminUser, type EventoAuditoria, type FiltroAuditoria } from './api';

export const ETIQUETAS_ACCION: Record<string, string> = {
  LOGIN: 'Inicio de sesión',
  LOGIN_FAILED: 'Inicio de sesión fallido',
  LOGOUT: 'Cierre de sesión',
  PROFILE_UPDATE: 'Perfil actualizado',
  PASSWORD_CHANGE: 'Cambio de contraseña',
  PASSWORD_CHANGE_FAILED: 'Cambio de contraseña fallido',
  UPLOAD: 'Subida',
  DOWNLOAD: 'Descarga',
  DELETE: 'Eliminación',
  TRASH: 'Enviado a la papelera',
  RESTORE: 'Restaurado de la papelera',
  PURGE: 'Eliminado definitivamente',
  TRASH_EMPTY: 'Papelera vaciada',
  TRASH_EXPIRE: 'Vencido en la papelera',
  MOVE: 'Movido o renombrado',
  FOLDER_CREATE: 'Carpeta creada',
  SHARE: 'Compartido',
  UNSHARE: 'Dejó de compartir',
  SHARED_DOWNLOAD: 'Descarga de compartido',
  PUBLIC_LINK_CREATE: 'Link público creado',
  PUBLIC_LINK_REVOKE: 'Link público revocado',
  PUBLIC_LINK_DOWNLOAD: 'Descarga por link público',
  OFFICE_OPEN: 'Abierto en el editor',
  OFFICE_SAVE: 'Guardado desde el editor',
  VERSION_RESTORE: 'Versión restaurada',
  VERSION_DOWNLOAD: 'Versión descargada',
  USER_CREATE: 'Usuario creado',
  USER_UPDATE: 'Usuario modificado',
  USER_DELETE: 'Usuario eliminado',
  USER_DELETE_PURGE: 'Usuario y archivos eliminados',
  V1_LOG: 'Registro de v1',
  V1_MIGRATION: 'Migración desde v1',
};

export function etiquetaAccion(accion: string): string {
  return ETIQUETAS_ACCION[accion] ?? accion;
}

// Eventos que conviene ver de un vistazo al recorrer la tabla.
const ALERTA = new Set(['LOGIN_FAILED', 'PASSWORD_CHANGE_FAILED', 'USER_DELETE_PURGE', 'PURGE', 'TRASH_EMPTY']);

type Campos = { actor: string; accion: string; desde: string; hasta: string; texto: string };

const VACIO: Campos = { actor: '', accion: '', desde: '', hasta: '', texto: '' };

/**
 * Las fechas del formulario son días locales: "hasta el 25" tiene que incluir
 * todo el 25 en Argentina, no cortar a las 21 h por tomarlo como UTC.
 */
function aFiltro(c: Campos): FiltroAuditoria {
  return {
    ...(c.actor ? { actor: c.actor } : {}),
    ...(c.accion ? { accion: c.accion } : {}),
    ...(c.desde ? { desde: new Date(`${c.desde}T00:00:00`).toISOString() } : {}),
    ...(c.hasta ? { hasta: new Date(`${c.hasta}T23:59:59.999`).toISOString() } : {}),
    ...(c.texto.trim() ? { texto: c.texto.trim() } : {}),
  };
}

export function Auditoria({ usuarios }: { usuarios: AdminUser[] }) {
  const [campos, setCampos] = useState<Campos>(VACIO);
  const [texto, setTexto] = useState('');
  const [acciones, setAcciones] = useState<string[]>([]);
  const [eventos, setEventos] = useState<EventoAuditoria[]>([]);
  const [siguiente, setSiguiente] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Si el filtro cambia mientras una búsqueda está en vuelo, su respuesta ya no
  // corresponde a lo que se ve en pantalla y se descarta.
  const turno = useRef(0);

  useEffect(() => {
    api.auditoriaAcciones().then(setAcciones, () => setAcciones([]));
  }, []);

  // El texto se escribe de corrido: se espera a que el usuario pare antes de buscar.
  useEffect(() => {
    const t = setTimeout(() => setCampos((c) => (c.texto === texto ? c : { ...c, texto })), 350);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    const miTurno = ++turno.current;
    setCargando(true);
    setError(null);
    api
      .auditoria(aFiltro(campos))
      .then((r) => {
        if (miTurno !== turno.current) return;
        setEventos(r.eventos);
        setSiguiente(r.siguiente);
      })
      .catch((err) => {
        if (miTurno !== turno.current) return;
        setError(err instanceof ApiError ? err.message : 'No pudimos cargar la auditoría.');
      })
      .finally(() => {
        if (miTurno === turno.current) setCargando(false);
      });
  }, [campos]);

  async function cargarMas() {
    if (!siguiente) return;
    const miTurno = turno.current;
    setCargando(true);
    try {
      const r = await api.auditoria(aFiltro(campos), siguiente);
      if (miTurno !== turno.current) return;
      setEventos((prev) => [...prev, ...r.eventos]);
      setSiguiente(r.siguiente);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar más eventos.');
    } finally {
      if (miTurno === turno.current) setCargando(false);
    }
  }

  function cambiar(campo: keyof Campos, valor: string) {
    setCampos((c) => ({ ...c, [campo]: valor }));
  }

  function limpiar() {
    setTexto('');
    setCampos(VACIO);
  }

  const hayFiltros = Object.values(campos).some(Boolean) || texto !== '';

  return (
    <div className="auditoria">
      <div className="filtros-auditoria">
        <label>
          <span>Usuario</span>
          <select value={campos.actor} onChange={(e) => cambiar('actor', e.target.value)}>
            <option value="">Todos</option>
            <option value="anonimo">Anónimo (links públicos)</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Acción</span>
          <select value={campos.accion} onChange={(e) => cambiar('accion', e.target.value)}>
            <option value="">Todas</option>
            {acciones
              .map((a) => ({ id: a, nombre: etiquetaAccion(a) }))
              .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nombre}
                </option>
              ))}
          </select>
        </label>

        <label>
          <span>Desde</span>
          <input
            type="date"
            value={campos.desde}
            max={campos.hasta || undefined}
            onChange={(e) => cambiar('desde', e.target.value)}
          />
        </label>

        <label>
          <span>Hasta</span>
          <input
            type="date"
            value={campos.hasta}
            min={campos.desde || undefined}
            onChange={(e) => cambiar('hasta', e.target.value)}
          />
        </label>

        <label className="filtro-texto">
          <span>Archivo, usuario afectado o IP</span>
          <input
            type="search"
            value={texto}
            placeholder="Buscar…"
            maxLength={200}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>

        <div className="filtros-acciones">
          {hayFiltros && (
            <button className="secundario" onClick={limpiar}>
              Limpiar
            </button>
          )}
          <a className="boton secundario" href={api.urlExportarAuditoria(aFiltro(campos))} download>
            Exportar CSV
          </a>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="tabla-scroll">
        <table className="tabla tabla-auditoria">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Acción</th>
              <th>Objetivo</th>
              <th>Detalle</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {eventos.map((e) => (
              <tr key={e.id}>
                <td className="col-fecha">{formatDate(e.fecha)}</td>
                <td>{e.actor ?? <span className="anonimo">anónimo</span>}</td>
                <td>
                  <span className={ALERTA.has(e.accion) ? 'accion alerta' : 'accion'} title={e.accion}>
                    {etiquetaAccion(e.accion)}
                  </span>
                </td>
                <td className="objetivo">{e.objetivo ?? '—'}</td>
                <td className="detalle">{e.detalle ?? ''}</td>
                <td className="mono ip">{e.ip ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!cargando && eventos.length === 0 && !error && (
        <p className="vacio">No hay eventos con estos filtros.</p>
      )}

      <div className="pie-auditoria">
        {cargando ? (
          <span className="sub">Cargando…</span>
        ) : (
          siguiente && (
            <button className="secundario" onClick={cargarMas}>
              Cargar más
            </button>
          )
        )}
      </div>
    </div>
  );
}
